import { useTranslation } from 'react-i18next';
import type { WeeklySummaryData } from '../services/api';
import { displayDigits } from '../utils/displayDigits';
import './weekly-comparison-chart.css';

type Metric = 1 | 2 | 3;

/** Paired daily values use one shared scale across both series. */
export function WeeklyComparisonChart({ daily, metric }: { daily: WeeklySummaryData['daily']; metric: Metric }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const number = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  const format = (value: number) => metric === 1 ? displayDigits(`${Math.floor(value / 3600)}:${String(Math.floor(value % 3600 / 60)).padStart(2, '0')}`, locale) : number.format(metric === 2 ? value / 1000 : Math.round(value));
  const days = daily.map(day => {
    const values = metric === 1 ? day.summary.total_time : metric === 2 ? day.summary.total_distance : day.summary.total_tss;
    const date = new Date(`${day.date.split('T')[0]}T12:00:00`);
    return {
      date: day.date,
      label: new Intl.DateTimeFormat(locale, { weekday: 'narrow' }).format(date),
      fullLabel: new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'short', day: 'numeric' }).format(date),
      planned: Math.max(0, Number(values.planned) || 0),
      actual: Math.max(0, Number(values.actual) || 0),
    };
  });
  const max = Math.max(1, ...days.flatMap(day => [day.planned, day.actual]));
  const plannedLabel = t('home.weekly.planned', 'Planned');
  const actualLabel = t('home.weekly.actual', 'Completed');
  return (
    <section className="weekly-comparison" aria-label={t('home.weekly.title')}>
      <div className="weekly-comparison__legend">
        <span><i className="weekly-comparison__key--planned" />{plannedLabel}</span>
        <span><i className="weekly-comparison__key--actual" />{actualLabel}</span>
        <small>{metric === 1 ? t('home.weekly.time') : metric === 2 ? t('home.trend.units.km') : 'TSS'}</small>
      </div>
      <div className="weekly-comparison__days">
        {days.map(day => (
          <div className="weekly-comparison__day" key={day.date} role="img"
            aria-label={`${day.fullLabel}: ${plannedLabel} ${format(day.planned)}; ${actualLabel} ${format(day.actual)}`}>
            <div className="weekly-comparison__numbers" aria-hidden="true">
              <span className="weekly-comparison__planned-value" title={`${plannedLabel}: ${format(day.planned)}`}>{day.planned > 0 ? format(day.planned) : '\u00a0'}</span>
              <strong title={`${actualLabel}: ${format(day.actual)}`}>{day.actual > 0 ? format(day.actual) : '\u00a0'}</strong>
            </div>
            <div className="weekly-comparison__bars" aria-hidden="true">
              <span className="weekly-comparison__bar--planned" style={{ height: `${day.planned / max * 100}%`, visibility: day.planned > 0 ? 'visible' : 'hidden' }} />
              <span className="weekly-comparison__bar--actual" style={{ height: `${day.actual / max * 100}%`, visibility: day.actual > 0 ? 'visible' : 'hidden' }} />
            </div>
            <span className="weekly-comparison__day-label" aria-hidden="true">{day.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}
